import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchLedgerJournals,
  updateLedgerJournals,
} from "services/company.service";
import { LedgerJournalCount } from "types/CustomerLedger";
import { ledgerKindLabel } from "utils/ledgerKind";
import {
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  SettingsCard,
  StatusBanner,
  useSaveWithToast,
} from "../ui";
import { httpStatusOf } from "../use-loaded-setting";

// "unknown": the first load has not ended. "hidden": the person may not see
// this (403) or the server has no such endpoint yet (404). "error": the load
// failed. "visible": the journals are loaded.
type Status = "unknown" | "visible" | "hidden" | "error";

/**
 * Journals excluded from turnover. Debt always uses every document so it can
 * reconcile with Finvalda; only the turnover figures respect this list.
 *
 * Nothing is shown until the load ends, so a person who may not see it never
 * sees it flash up before the 403 hides it. A failed load shows an error with
 * Retry and no list: with a list that was never loaded, Save would send an
 * empty exclusion list and wipe the company's real one.
 */
export default function LedgerJournalsCard({
  companyId,
}: {
  companyId: string;
}) {
  const { t } = useTranslation();
  const { save, saving } = useSaveWithToast();
  const [status, setStatus] = useState<Status>("unknown");
  const [journals, setJournals] = useState<LedgerJournalCount[]>([]);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const res = await fetchLedgerJournals(companyId);
      setJournals(res.journals || []);
      setExcluded(new Set(res.excluded || []));
      setStatus("visible");
    } catch (err) {
      const httpStatus = httpStatusOf(err);
      setStatus(httpStatus === 403 || httpStatus === 404 ? "hidden" : "error");
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (journal: string) => {
    const next = new Set(excluded);
    if (next.has(journal)) {
      next.delete(journal);
    } else {
      next.add(journal);
    }
    setExcluded(next);
  };

  const submit = () =>
    save(
      async () => {
        const res = await updateLedgerJournals(companyId, Array.from(excluded));
        setJournals(res.journals || []);
        setExcluded(new Set(res.excluded || []));
      },
      {
        errorMessage: (err: any) =>
          err?.response?.data?.message || t("ledger_journals_save_failed"),
      }
    );

  if (status === "unknown" || status === "hidden") return null;

  if (status === "error") {
    return (
      <StatusBanner
        tone="error"
        title={t("ledger_journals_load_failed")}
        action={
          <button type="button" className={SECONDARY_BUTTON} onClick={load}>
            {t("retry")}
          </button>
        }
      />
    );
  }

  return (
    <SettingsCard
      title={t("ledger_journals_title")}
      description={t("ledger_journals_hint")}
    >
      {journals.length === 0 ? (
        <p className="text-sm text-gray-600">{t("no_data")}</p>
      ) : (
        <ul className="mb-4 space-y-2">
          {journals.map((journal) => (
            <li
              key={journal.journal}
              className="flex items-start gap-2 text-sm"
            >
              <input
                id={`ledger-journal-${journal.journal}`}
                type="checkbox"
                className="mt-0.5"
                checked={excluded.has(journal.journal)}
                onChange={() => toggle(journal.journal)}
              />
              <label
                htmlFor={`ledger-journal-${journal.journal}`}
                className="text-gray-700"
              >
                <span className="font-medium">{journal.journal}</span>
                {" — "}
                {Object.entries(journal.kinds)
                  .map(
                    ([kind, count]) => `${ledgerKindLabel(t, kind)} ${count}`
                  )
                  .join(", ")}
              </label>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className={PRIMARY_BUTTON}
        disabled={saving}
        onClick={submit}
      >
        {t("settings_save")}
      </button>
    </SettingsCard>
  );
}
